/** Scene + file IO for the drawing layer — every request rides the typed
 * client (`api.request`), keeping the house rule that no raw URLs leave
 * `lib/api.ts` (the one exception is the pagehide beacon in save-controller,
 * which mirrors the InkModel's old unload drain). */

import type { BinaryFileData, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/excalidraw/element/types";

import { api, type DrawingFilePayload, type DrawingOut, type DrawingScene } from "@/lib/api";

/** The appState keys that persist (mirrors the server's allowlist — everything
 * else is per-session view state that must never round-trip). */
const APPSTATE_ALLOW = [
  "viewBackgroundColor",
  "gridSize",
  "gridModeEnabled",
  "gm_pages",
] as const;

export function persistedAppState(appState: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of APPSTATE_ALLOW) {
    if (appState[key] !== undefined && appState[key] !== null) out[key] = appState[key];
  }
  return out;
}

/** Build the wire scene from the editor's live state. Deleted elements are
 * dropped — the server is not a sync peer that needs tombstones; the whole
 * scene replaces the previous one. */
export function toWireScene(
  elements: readonly OrderedExcalidrawElement[],
  appState: Record<string, unknown>,
): DrawingScene {
  const live = elements.filter((e) => !e.isDeleted);
  const fileIds = new Set<string>();
  for (const e of live) {
    const fileId = (e as { fileId?: string | null }).fileId;
    if (fileId) fileIds.add(fileId);
  }
  return {
    schema_version: 1,
    elements: live as unknown as Record<string, unknown>[],
    appState: persistedAppState(appState),
    file_ids: [...fileIds],
  };
}

export function getDrawing(docId: string): Promise<DrawingOut> {
  return api.request<DrawingOut>("GET", `/api/documents/${docId}/drawing`);
}

export function putDrawing(docId: string, scene: DrawingScene): Promise<{ updated_at: string }> {
  return api.request<{ updated_at: string }>("PUT", `/api/documents/${docId}/drawing`, scene);
}

export async function getDrawingFile(
  docId: string,
  fileId: string,
): Promise<DrawingFilePayload | null> {
  try {
    return await api.request<DrawingFilePayload>(
      "GET",
      `/api/documents/${docId}/drawing/files/${fileId}`,
    );
  } catch {
    return null; // a missing file renders as Excalidraw's broken-image tile
  }
}

export function putDrawingFile(
  docId: string,
  fileId: string,
  payload: DrawingFilePayload,
): Promise<{ file_id: string }> {
  return api.request<{ file_id: string }>(
    "PUT",
    `/api/documents/${docId}/drawing/files/${fileId}`,
    payload,
  );
}

/** Fetch the bytes for every fileId the scene references, as the BinaryFiles
 * map Excalidraw's `addFiles`/initialData wants. Missing files are skipped. */
export async function fetchSceneFiles(docId: string, fileIds: string[]): Promise<BinaryFiles> {
  const files: BinaryFiles = {};
  const results = await Promise.all(
    fileIds.map(async (id) => ({ id, payload: await getDrawingFile(docId, id) })),
  );
  for (const { id, payload } of results) {
    if (!payload) continue;
    files[id as BinaryFileData["id"]] = {
      id: id as BinaryFileData["id"],
      mimeType: payload.mimeType as BinaryFileData["mimeType"],
      dataURL: payload.dataURL as BinaryFileData["dataURL"],
      created: Date.now(),
    };
  }
  return files;
}

/** Upload any referenced file the server doesn't have yet. `known` accumulates
 * across a session so each file uploads at most once (fileIds are content
 * hashes, so the server-side PUT is idempotent anyway). */
export async function uploadNewFiles(
  docId: string,
  scene: DrawingScene,
  files: BinaryFiles,
  known: Set<string>,
): Promise<void> {
  const pending = scene.file_ids
    .filter((id) => !known.has(id))
    .map((id) => ({ id, f: files[id as BinaryFileData["id"]] }))
    .filter((p): p is { id: string; f: BinaryFileData } => !!p.f);
  await Promise.all(
    pending.map(async ({ id, f }) => {
      await putDrawingFile(docId, id, { mimeType: f.mimeType, dataURL: f.dataURL });
      known.add(id);
    }),
  );
}
